<template>
    <div
        ref="anchor"
        :class="['dropdown basic-dropdown', { focused: isListOpen }]"
    >
        <div
            :id="id"
            tabindex="0"
            :class="'input-no-border selected-' + modelValue"
            @click="onInputClick"
            @keydown.enter.prevent="onKeyboardConfirm"
            @keydown.space.prevent="onKeyboardConfirm"
            @keydown.down.prevent="onArrowDown"
            @keydown.up.prevent="onArrowUp"
            @keydown.esc="onEsc"
        >
            {{ optionName }}
        </div>
        <teleport to="body">
            <div
                v-if="isListOpen"
                :id="id + '-options'"
                ref="list"
                v-focus-out="onCloseList"
                :class="[
                    'list-wrapper',
                    { 'list-wrapper--flipped': isListFlipped },
                ]"
                :style="listStyle"
            >
                <ul>
                    <li
                        v-if="nullable"
                        :class="itemClass()"
                        @click="onSelectEmptyOption()"
                        @mouseover="highlightedValue = null"
                    >
                        &nbsp;
                    </li>
                    <li
                        v-for="(option, index) in $enumOptions(group)"
                        :key="`option-${index}`"
                        :class="itemClass(option[0])"
                        @click="onSelectOption(option)"
                        @mouseover="highlightedValue = option[0]"
                    >
                        {{ option[1] }}
                    </li>
                </ul>
            </div>
        </teleport>
    </div>
</template>
<script>
import BaseDropdownPosition from "js/components/base/BaseDropdownPosition.js";

export default {
    name: "Dropdown",
    extends: BaseDropdownPosition,
    props: {
        id: {
            type: String,
            default: null,
        },
        group: {
            type: String,
            required: true,
        },
        modelValue: {
            type: [String, null],
            required: true,
        },
        nullable: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["update:modelValue"],
    data() {
        return {
            isListOpen: false,
            highlightedValue: "",
        };
    },
    computed: {
        optionName() {
            return this.$enumGet(this.group, this.modelValue);
        },
        keyboardValues() {
            const codes = this.$enumOptions(this.group).map(([code]) => code);

            return this.nullable ? [null, ...codes] : codes;
        },
    },
    methods: {
        onCloseList() {
            this.isListOpen = false;
            this.highlightedValue = "";
        },
        onEsc() {
            this.onCloseList();
        },
        onSelectOption(option) {
            this.$emit("update:modelValue", option[0]);
            this.onCloseList();
        },
        onSelectEmptyOption() {
            this.$emit("update:modelValue", null);
            this.onCloseList();
        },
        onInputClick() {
            this.isListOpen = true;
            this.highlightedValue = this.modelValue ?? "";
        },
        onKeyboardConfirm() {
            if (!this.isListOpen) {
                this.onInputClick();

                return;
            }

            if (this.highlightedValue === null) {
                this.onSelectEmptyOption();

                return;
            }

            const option = this.$enumOptions(this.group).find(
                ([code]) => code === this.highlightedValue
            );

            if (option) {
                this.onSelectOption(option);
            }
        },
        onArrowDown() {
            if (!this.isListOpen) {
                this.onInputClick();
            }

            this.highlightedValue = this.stepHighlighted(
                this.keyboardValues,
                this.highlightedValue,
                1
            );
        },
        onArrowUp() {
            if (!this.isListOpen) {
                this.onInputClick();
            }

            this.highlightedValue = this.stepHighlighted(
                this.keyboardValues,
                this.highlightedValue,
                -1
            );
        },
        itemClass(option) {
            let cssClasses = {
                highlighted: this.highlightedValue === (option || null),
            };
            if (option) {
                cssClasses["option-" + option] = true;
            } else {
                cssClasses["option-empty"] = true;
            }
            return cssClasses;
        },
    },
};
</script>
